import { useState } from 'react';
import { MoreHorizontal, LogOut, Settings as SettingsIcon, User, Hexagon, Sparkles } from 'lucide-react';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { hapticLight } from '../utils/haptics';

const PRIMARY_VIEW = { main: 'overview', operations: 'services', financials: 'payments', customers: 'relations' };
const PINNED_IDS = ['main', 'operations', 'financials', 'customers'];

/** Native bottom tab bar — mobile only (<lg). Pinned arena tabs + a More
 *  bottom sheet with every destination. Design system: N7 (bar) + M7 (sheet). */
export const MobileTabBar = ({ navGroups, activeView, onSelect, isAdmin, showIntelligence, onIntelligence, onSettings, onLogout }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const pinned = PINNED_IDS.map((id) => navGroups.find((g) => g.id === id && g.items?.length)).filter(Boolean);
  const pinnedActiveId = pinned.find((g) => g.items.some((i) => i.id === activeView))?.id || null;
  const moreActive = !pinnedActiveId;

  const go = (viewId) => {
    hapticLight();
    setMoreOpen(false);
    onSelect(viewId);
  };

  return (
    <>
      <nav
        data-testid="mobile-tab-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-[45] bg-white/[0.97] backdrop-blur-xl shadow-[0_-8px_24px_rgba(15,23,42,0.07)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex h-16 items-stretch justify-around px-1">
          {pinned.map((g) => {
            const Icon = g.icon;
            const active = pinnedActiveId === g.id;
            const primary = PRIMARY_VIEW[g.id];
            const target = g.items.some((i) => i.id === primary) ? primary : g.items[0].id;
            return (
              <button key={g.id} data-testid={`mobile-tab-${g.id}`} onClick={() => go(target)} className="flex flex-1 flex-col items-center justify-center gap-1">
                <span className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors ${active ? 'bg-blue-600/10 text-blue-600' : 'text-gray-400'}`}>
                  <Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.25 : 1.75} />
                </span>
                <span className={`text-[10px] leading-none ${active ? 'font-bold text-blue-600' : 'font-semibold text-gray-500'}`}>{g.label}</span>
              </button>
            );
          })}
          <button data-testid="mobile-tab-more" onClick={() => { hapticLight(); setMoreOpen(true); }} className="flex flex-1 flex-col items-center justify-center gap-1">
            <span className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors ${moreActive ? 'bg-blue-600/10 text-blue-600' : 'text-gray-400'}`}>
              <MoreHorizontal className="h-[18px] w-[18px]" strokeWidth={moreActive ? 2.25 : 1.75} />
            </span>
            <span className={`text-[10px] leading-none ${moreActive ? 'font-bold text-blue-600' : 'font-semibold text-gray-500'}`}>More</span>
          </button>
        </div>
      </nav>

      <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
        <DrawerContent
          data-testid="mobile-more-sheet"
          className="z-[75] max-h-[85vh] rounded-t-3xl border-0 bg-white"
          overlayClassName="z-[70] bg-gray-900/50 backdrop-blur-[2px]"
        >
          <DrawerTitle className="sr-only">All destinations</DrawerTitle>
          <div className="overflow-y-auto px-5 pt-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}>
            {navGroups.map((group) => (group.items?.length ? (
              <div key={group.id} className="mb-4">
                <p className="px-1 pb-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-gray-400">{group.label}</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = activeView === item.id;
                    return (
                      <button key={item.id} data-testid={`more-nav-${item.id}`} onClick={() => go(item.id)} className="flex flex-col items-center gap-1.5 rounded-2xl py-2">
                        <span className={`flex h-12 w-12 items-center justify-center rounded-2xl transition-colors ${active ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-500'}`}>
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </span>
                        <span className={`text-center text-[11px] leading-tight ${active ? 'font-bold text-blue-600' : 'font-medium text-gray-600'}`}>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null))}

            <div className="space-y-1 border-t border-gray-100 pt-3">
              {showIntelligence && (
                <button data-testid="more-nav-intelligence" onClick={() => { setMoreOpen(false); onIntelligence(); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white"><Hexagon className="h-4 w-4" strokeWidth={1.75} /></span>
                  Intelligence
                  <Sparkles className="ml-auto h-4 w-4 text-blue-600" />
                </button>
              )}
              <button data-testid="more-nav-settings" onClick={() => { setMoreOpen(false); onSettings(); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">{isAdmin ? <SettingsIcon className="h-4 w-4" strokeWidth={1.75} /> : <User className="h-4 w-4" strokeWidth={1.75} />}</span>
                {isAdmin ? 'Settings' : 'My Profile'}
              </button>
              <button data-testid="more-nav-logout" onClick={() => { setMoreOpen(false); onLogout(); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600"><LogOut className="h-4 w-4" strokeWidth={1.75} /></span>
                Logout
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default MobileTabBar;
